import { useCallback, useEffect, useRef, useState } from "react";
import api, { API_BASE, tokenStore } from "../../../lib/api";
import { DEFAULT_SETTINGS, sanitizeSettings } from "../../../lib/chartSeries";

const LS_KEY = "estima_loan_chart_prefs_v1";

function readLocal() {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_KEY) || "null");
    return { settings: sanitizeSettings(raw), savedAt: Number(raw?._saved_at) || 0 };
  } catch {
    return { settings: sanitizeSettings(null), savedAt: 0 };
  }
}

function sendNow(payload) {
  const token = tokenStore.access;
  try {
    fetch(`${API_BASE}/user/chart-preferences`, {
      method: "PUT", keepalive: true, credentials: "include",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ chart_preferences: payload }),
    }).catch(() => { /* account sync is best-effort */ });
  } catch { /* fetch unavailable — browser copy is kept */ }
}

// Browser copy always; account copy (NEW optional `chart_preferences`) when signed in.
// The newer of the two (by _saved_at) wins on load; pending saves flush on leave.
export function useChartPrefs(isAuthed) {
  const initial = useRef(readLocal());
  const [settings, setSettings] = useState(initial.current.settings);
  const savedAt = useRef(initial.current.savedAt);
  const pending = useRef(null);

  useEffect(() => {
    if (!isAuthed) return undefined;
    let cancelled = false;
    api.get("/user/chart-preferences")
      .then(({ data }) => {
        const remote = data?.chart_preferences;
        if (!cancelled && remote && Number(remote._saved_at || 0) > savedAt.current) {
          savedAt.current = Number(remote._saved_at || 0);
          setSettings(sanitizeSettings(remote));
        }
      })
      .catch(() => { /* offline or signed out — the browser copy is still used */ });
    return () => { cancelled = true; };
  }, [isAuthed]);

  useEffect(() => {
    if (!isAuthed) return undefined;
    const flush = () => { if (pending.current) { sendNow(pending.current); pending.current = null; } };
    window.addEventListener("pagehide", flush);
    return () => { window.removeEventListener("pagehide", flush); flush(); };
  }, [isAuthed]);

  const persist = useCallback((next) => {
    const stamp = Date.now();
    savedAt.current = stamp;
    const payload = { ...next, _saved_at: stamp };
    try { localStorage.setItem(LS_KEY, JSON.stringify(payload)); } catch { /* storage blocked — ignore */ }
    if (isAuthed) pending.current = payload;
  }, [isAuthed]);

  useEffect(() => {
    if (!isAuthed) return undefined;
    const t = setInterval(() => {
      if (!pending.current) return;
      const p = pending.current;
      pending.current = null;
      api.put("/user/chart-preferences", { chart_preferences: p }).catch(() => { pending.current = pending.current || p; });
    }, 800);
    return () => clearInterval(t);
  }, [isAuthed]);

  const update = useCallback((patch) => {
    setSettings((s) => {
      const next = sanitizeSettings({ ...s, ...patch });
      persist(next);
      return next;
    });
  }, [persist]);
  const reset = useCallback(() => {
    const next = sanitizeSettings(DEFAULT_SETTINGS);
    persist(next);
    setSettings(next);
  }, [persist]);
  return [settings, update, reset];
}
